/**
 * OpenSessionModal — opens a cash-desk session (POST /caisse/sessions, cap finance.caisse.open).
 * Two fields: caisse id + opening float. Keyboard-first (autofocus, Enter submits, Esc closes).
 * The submit is gated by useCan; the server re-checks regardless.
 */
import { useState, type FormEvent } from "react";
import { Button, FormField, Input, Modal, useToast } from "@/ui";
import { useCan } from "@/auth";
import { useT } from "@/app";
import { ApiError } from "@/api";
import { FINANCE_CAPS, useLabId, useOpenSession } from "../api";
import { MoneyInput } from "./MoneyInput";

export interface OpenSessionModalProps {
  open: boolean;
  onClose: () => void;
  onOpened: () => void;
}

export function OpenSessionModal({ open, onClose, onOpened }: OpenSessionModalProps) {
  const { t } = useT();
  const toast = useToast();
  const labId = useLabId();
  const canOpen = useCan(FINANCE_CAPS.caisseOpen);
  const mutation = useOpenSession(labId);

  const [caisseId, setCaisseId] = useState("");
  const [fond, setFond] = useState<number | null>(0);
  const [touched, setTouched] = useState(false);

  const caisseIdError = touched && caisseId.trim() === "" ? t("finance.field.required") : null;
  const fondError = touched && (fond == null || fond < 0) ? t("finance.field.amountInvalid") : null;
  const valid = caisseId.trim() !== "" && fond != null && fond >= 0;

  const reset = () => {
    setCaisseId("");
    setFond(0);
    setTouched(false);
  };

  const handleClose = () => {
    if (mutation.isPending) return;
    reset();
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid || !canOpen) return;
    mutation.mutate(
      { caisse_id: caisseId.trim(), fond_de_caisse: fond ?? 0 },
      {
        onSuccess: () => {
          toast.success(t("finance.caisse.open.success"));
          reset();
          onOpened();
        },
        onError: (err) => {
          toast.error(err instanceof ApiError ? err.message : t("finance.field.required"));
        },
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("finance.caisse.open.title")}
      footer={
        <>
          <Button variant="ghost" onClick={handleClose} disabled={mutation.isPending}>
            {t("action.close")}
          </Button>
          <Button
            type="submit"
            form="finance-open-session"
            loading={mutation.isPending}
            disabled={!canOpen}
          >
            {t("finance.caisse.open.submit")}
          </Button>
        </>
      }
    >
      <form id="finance-open-session" onSubmit={submit} noValidate>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          <FormField
            label={t("finance.caisse.open.caisseId")}
            hint={t("finance.caisse.open.caisseId.hint")}
            required
            {...(caisseIdError ? { error: caisseIdError } : {})}
          >
            {(p) => (
              <Input
                {...p}
                autoFocus
                autoComplete="off"
                value={caisseId}
                invalid={!!caisseIdError}
                placeholder="CAISSE-01"
                onChange={(e) => setCaisseId(e.target.value)}
              />
            )}
          </FormField>

          <FormField
            label={t("finance.caisse.open.fond")}
            hint={t("finance.caisse.open.fond.hint")}
            required
            {...(fondError ? { error: fondError } : {})}
          >
            {(p) => (
              <MoneyInput
                {...p}
                aria-label={t("finance.amount.aria")}
                valueCentimes={fond}
                onChangeCentimes={setFond}
                invalid={!!fondError}
              />
            )}
          </FormField>
        </div>
      </form>
    </Modal>
  );
}
